import { Trans, useLingui } from "@lingui/react/macro";
import { useForm } from "react-hook-form";
import Form from "@/components/Form/Form";
import Select from "@medoma/components/Select/Select";
import { FilledButton } from "@medoma/components/Button/Button";
import {
  createShift,
  NO_EMPLOYEE,
  shiftKeys,
  useShiftPresets,
  type INewShift,
} from "@/api/Shifts";
import { useEmployees } from "@/api/Employee";
import {
  medicalCompetenceDictionary,
  shiftTypesDictionary,
  type IShiftType,
} from "@medoma/models/shifts";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { setTimeOnDate } from "@/Utils/DateUtils";
import { useTeams } from "@/api/Patients";
import { ShiftTemplateItem } from "@/pages/commandcenter/Shifts/ShiftTemplateItem";
import styles from "./NewShiftFromTemplateFrom.module.scss";
import * as Sentry from "@sentry/react";
import {
  compareEmployeesByCompetence,
  employeeName,
} from "@medoma/models/shifts";
import { shiftTypesByOrganization } from "@medoma/models/shiftTypesByOrganization";
import { useUserProfile } from "@/api/Users";

const NO_TEAM = 0;
const NO_SHIFT_TYPE = "";

type NewShiftFromTemplateFormValues = {
  employeeId: number | null;
  teamId: number | null;
  shiftType: IShiftType | null | typeof NO_SHIFT_TYPE;
};

const NewShiftFromTemplateForm = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedDate = useSelectedDate();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { templateId } = useParams();
  const { organization } = useUserProfile();

  const {
    data: shiftTemplates,
    isPending: shiftTemplatesPending,
    isError: shiftTemplatesError,
    error: errorShiftTemplates,
  } = useShiftPresets();

  const {
    data: employees,
    isPending: employeesPending,
    isError: employeesError,
  } = useEmployees();

  const {
    data: teams,
    isPending: teamsPending,
    isError: teamsError,
  } = useTeams();

  const shiftTypes = shiftTypesByOrganization[organization];

  const { register, handleSubmit } = useForm<NewShiftFromTemplateFormValues>({
    defaultValues: {
      employeeId: NO_EMPLOYEE,
      teamId: NO_TEAM,
      shiftType: NO_SHIFT_TYPE,
    },
  });

  const { mutate: createShiftMutation, isPending: isCreatingShift } =
    useMutation({
      mutationFn: (newShift: INewShift) => createShift(newShift),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
        void navigate("..");
      },
      onError: (error) => {
        displayErrorMessageAlert(deducedError(error));
      },
    });

  if (teamsPending || employeesPending || shiftTemplatesPending) {
    return <Loading message={t`Hämtar information om nytt arbetspass`} />;
  }

  const chosenTemplate = shiftTemplates?.find(
    (template) => template.id === Number(templateId),
  );

  if (teamsError || employeesError || shiftTemplatesError || !chosenTemplate) {
    Sentry.captureException(errorShiftTemplates);
    return (
      <ErrorMessage
        message={t`Kunde inte hämta information om nytt arbetspass. Prova att uppdatera sidan. Kontakta support om felet kvarstår.`}
      />
    );
  }

  const {
    startTime,
    endTime,
    competence,
    shiftType: templateShiftType,
  } = chosenTemplate;

  const matchingEmployees = employees.filter(
    (e) => e.competence === competence,
  );
  const otherEmployees = employees
    .filter((e) => e.competence !== competence)
    .sort(compareEmployeesByCompetence);

  const validateAndSubmit = handleSubmit((formData) => {
    const { employeeId, teamId, shiftType: formShiftType } = formData;
    const endsNextDay = startTime > endTime;

    const newShift: INewShift = {
      startDateTime: setTimeOnDate({
        selectedDate,
        time: startTime,
        addedDays: 0,
      }).toISOString(),
      endDateTime: setTimeOnDate({
        selectedDate,
        time: endTime,
        addedDays: endsNextDay ? 1 : 0,
      }).toISOString(),
      competence,
      employeeId: employeeId === NO_EMPLOYEE ? null : employeeId,
      teamId: teamId === NO_TEAM ? null : teamId,
      shiftType: templateShiftType || formShiftType || null,
    };
    createShiftMutation(newShift);
  });

  return (
    <div className={styles.formWrapper}>
      <div className={styles.shiftTemplate}>
        <ShiftTemplateItem template={chosenTemplate} />
      </div>
      <Form onSubmit={validateAndSubmit}>
        <Form.Row>
          <Select
            {...register("employeeId", { valueAsNumber: true })}
            label={t`Medarbetare`}
          >
            <option value={NO_EMPLOYEE}>
              <Trans>Ingen</Trans>
            </option>
            <optgroup label={t(medicalCompetenceDictionary[competence].long)}>
              {matchingEmployees.length > 0 ? (
                matchingEmployees.map((employee) => {
                  return (
                    <option key={employee.id} value={employee.id}>
                      {employeeName(employee, { employeeNameFormat: "full" })}
                      {employee.distinctionLabel
                        ? ` (${employee.distinctionLabel})`
                        : null}
                    </option>
                  );
                })
              ) : (
                <option disabled>
                  <Trans>Ingen medarbetare med denna kompetens</Trans>
                </option>
              )}
            </optgroup>
            <optgroup label={t`Övriga`}>
              {otherEmployees.map(
                ({ id, firstName, lastName, competence, distinctionLabel }) => (
                  <option key={id} value={id}>
                    {employeeName(
                      { firstName, lastName },
                      { employeeNameFormat: "full" },
                    )}
                    {competence
                      ? `, ${t(medicalCompetenceDictionary[competence].long)}`
                      : null}
                    {distinctionLabel ? ` (${distinctionLabel})` : null}
                  </option>
                ),
              )}
            </optgroup>
          </Select>
        </Form.Row>

        {teams.length > 0 ? (
          <Form.Row>
            <Select
              {...register("teamId", {
                valueAsNumber: true,
              })}
              label={t`Team`}
            >
              <option value={NO_TEAM}>
                <Trans>Inget</Trans>
              </option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.displayName}
                </option>
              ))}
            </Select>
          </Form.Row>
        ) : null}

        {templateShiftType || shiftTypes.length === 0 ? null : (
          <Form.Row>
            <Select {...register("shiftType")} label={t`Typ`}>
              <option value={NO_SHIFT_TYPE}>
                <Trans>Ingen</Trans>
              </option>
              {shiftTypes.map((type) => (
                <option key={type} value={type}>
                  {t(shiftTypesDictionary[type])}
                </option>
              ))}
            </Select>
          </Form.Row>
        )}

        <Form.Row type="flex">
          <FilledButton type="submit" disabled={isCreatingShift}>
            <Trans>Skapa arbetspass</Trans>
          </FilledButton>
        </Form.Row>
      </Form>
    </div>
  );
};

export default NewShiftFromTemplateForm;
